import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';
import { resetPassword } from '../services/api';
import PublicNav from '../components/PublicNav';

export default function ResetPasswordPage() {
  const navigate      = useNavigate();
  const [token,       setToken]       = useState('');
  const [password,    setPassword]    = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [done,        setDone]        = useState(false);
  const [error,       setError]       = useState('');
  // MFA challenge — surfaced when the backend says the account has 2FA.
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode,     setMfaCode]     = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) setError('Missing or invalid reset link. Please request a new one.');
    else setToken(t);
  }, []);

  const passwordsMatch = password && confirm && password === confirm;
  const strongEnough   = password.length >= 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token)           return;
    if (!strongEnough)    { setError('Password must be at least 8 characters.'); return; }
    if (!passwordsMatch)  { setError('Passwords do not match.'); return; }
    if (mfaRequired && !mfaCode) { setError('Enter your authenticator or backup code to continue.'); return; }

    setLoading(true);
    setError('');
    try {
      await resetPassword(token, password, mfaRequired ? mfaCode : undefined);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      // Backend returns 401 with mfaRequired:true when this account has 2FA
      // enabled and no code was provided. Switch the form into MFA mode.
      const body = err?.responseBody;
      if (body?.error?.mfaRequired) {
        setMfaRequired(true);
        setError('Two-factor authentication is enabled on this account. Enter your authenticator code or one of your backup codes to finish the reset.');
      } else {
        setError(err.message || 'Reset failed. The link may have expired — please request a new one.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 flex flex-col">
      <PublicNav />
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 mb-4 shadow-lg shadow-violet-500/20">
            <span className="text-white font-bold text-xl">M</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Set new password</h1>
          <p className="text-gray-400 mt-1 text-sm">Choose a strong password for your account.</p>
        </div>

        <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-8">
          {done ? (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 mx-auto">
                <CheckCircle className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-white font-semibold text-lg">Password updated!</h2>
              <p className="text-gray-400 text-sm">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {!token && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 flex gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-amber-300 text-sm">Invalid reset link. Please request a new one.</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">New password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    className="input w-full pl-10 pr-10"
                    autoFocus
                  />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {password && (
                  <p className={`text-xs mt-1.5 ${strongEnough ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {strongEnough ? 'Strong enough' : 'Too short — use at least 8 characters'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Confirm password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Repeat new password"
                    required
                    className="input w-full pl-10"
                  />
                </div>
                {confirm && (
                  <p className={`text-xs mt-1.5 ${passwordsMatch ? 'text-emerald-400' : 'text-red-400'}`}>
                    {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                  </p>
                )}
              </div>

              {/* MFA challenge — only when backend asked for it */}
              {mfaRequired && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    Authenticator code <span className="text-gray-500 font-normal">— or backup code</span>
                  </label>
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.toUpperCase().slice(0, 9))}
                    placeholder="000000 or XXXX-XXXX"
                    autoFocus
                    className="input w-full font-mono tracking-widest text-center"
                  />
                  <p className="text-[11px] text-gray-500 mt-1.5">
                    Two-factor authentication is enabled on this account. Enter the 6-digit code from your authenticator app, or one of your 8-character backup codes.
                  </p>
                </div>
              )}

              {error && (
                <div className={`rounded-xl px-4 py-3 border ${
                  mfaRequired ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20'
                }`}>
                  <p className={`text-sm ${mfaRequired ? 'text-amber-300' : 'text-red-400'}`}>{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !token || !strongEnough || !passwordsMatch || (mfaRequired && !mfaCode)}
                className="btn-primary w-full"
              >
                {loading ? 'Updating…' : mfaRequired ? 'Verify code & update password' : 'Update password'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center mt-6 text-sm text-gray-400">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-violet-400 hover:text-violet-300 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to sign in
          </Link>
        </p>
      </div>
      </div>
    </div>
  );
}
